import React from "react";
import { useState, useMemo, useContext, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import motionify from "../../components/Motion";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import apiService from "../../services/ApiService";
import FolderPathButton from "../../components/FolderPathButton";

import ModelGridFolder from "../../components/ModelFolder/ModelGridFolder";
import ModelGridItem from "../../components/ModelItem/ModelGridItem";
import ModelListFolder from "../../components/ModelFolder/ModelListFolder";
import ModelListItem from "../../components/ModelItem/ModelListItem";
import ModelDragLayer from "../../components/ModelDragLayer";

import { useAppStore } from "../../shared/stores/useAppStore";
import TutorialStep, { TutorialContext } from "../../components/Tutorial";
import { cn } from "../../lib/utils";
import TailwindButton from "../../components/Tailwind/TailwindButton";
import TailwindModal from "../../components/Tailwind/TailwindModal";
import TailwindInput from "../../components/Tailwind/TailwindInput";

import { useModels, MODELS_QUERY_KEY } from "../../shared/hooks/queries/useModels";
import {
  useCreateModel,
  useCreateFolder,
  useUpdateModel,
  useUpdateFolder,
  useMoveSharedModel,
} from "../../shared/hooks/mutations/useModelMutations";
import type { FolderTreeResponse, Folder, ModelMeta } from "../../shared/types";

export interface MyModelsPageProps {
}

export interface MyModelMetadata {
    id: number;
    name: string;
    folderId: number;
    editing: boolean;
    deleteModalOpen: boolean;
    isShared: boolean;
    modelScenarios: Array<number>;
    isNew: boolean;
    currentScenario: number | null;
    modelRulesDeducted: any;
}

export interface MyModelsFolder {
    id: number;
    name: string;
    folderId: number | null;
    folderChildrensIds: Array<number>;
    modelChildrensIds: Array<number>;
    sharedModelChildrensIds: Array<number>;
    editing: boolean;
    deleteModalOpen: boolean;
    expanded: boolean;
}

export interface MyModelsPageState {
    rootFolderId: string;
    folders: Record<string, MyModelsFolder>;
    models: Record<string, MyModelMetadata>;
    sharedModels: Record<string, MyModelMetadata>;
}

function mapFolders(folders: Folder[]): Record<string, MyModelsFolder> {
    const result: Record<string, MyModelsFolder> = {};
    for (const elem of folders) {
        result[elem.id.toString()] = {
            id: elem.id,
            name: elem.name,
            folderId: elem.folder_id,
            folderChildrensIds: elem.folder_childrens_ids,
            modelChildrensIds: elem.model_childrens_ids,
            sharedModelChildrensIds: elem.shared_model_childrens_ids,
            editing: false,
            deleteModalOpen: false,
            expanded: false,
        };
    }
    return result;
}

function mapModels(models: ModelMeta[]): Record<string, MyModelMetadata> {
    const result: Record<string, MyModelMetadata> = {};
    for (const elem of models) {
        result[elem.id.toString()] = {
            id: elem.id,
            name: elem.name,
            folderId: elem.folder_id,
            editing: false,
            deleteModalOpen: false,
            modelScenarios: elem.model_scenarios,
            isNew: elem.is_new,
            isShared: elem.is_shared,
            currentScenario: elem.model_scenarios[0] ?? null,
            modelRulesDeducted: elem.model_rules_deducted,
        };
    }
    return result;
}

function mapApiFolderTree(response: FolderTreeResponse): MyModelsPageState {
    return {
        rootFolderId: response.root_folder_id.toString(),
        folders: mapFolders(response.folders),
        models: mapModels(response.models),
        sharedModels: mapModels(response.shared_models),
    };
}

const DEFAULT_STATE: MyModelsPageState = {
    rootFolderId: '0',
    folders: {
        '0': {
            id: 0,
            name: 'root',
            folderChildrensIds: [],
            modelChildrensIds: [],
            sharedModelChildrensIds: [],
            folderId: null,
            editing: false,
            deleteModalOpen: false,
            expanded: false,
        },
    },
    models: {},
    sharedModels: {},
};

const EmptyState = ({ filter }: { filter: 'all' | 'mymodels' | 'shared' }) => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="size-16 rounded-full bg-slate-50 flex items-center justify-center mb-4">
            <span className="material-symbols-outlined text-[32px] text-slate-300">
                {filter === 'shared' ? 'group' : 'folder_open'}
            </span>
        </div>
        {filter === 'shared' ? (
            <>
                <p className="text-sm font-medium text-slate-500">No models shared with you yet</p>
                <p className="text-xs text-slate-400 mt-1">Models shared by other users will appear here</p>
            </>
        ) : (
            <>
                <p className="text-sm font-medium text-slate-500">This folder is empty</p>
                <p className="text-xs text-slate-400 mt-1">Create a model or folder to get started</p>
            </>
        )}
    </div>
);

export default function MyModels() {
    const commercialPlan = useAppStore(s => s.commercialPlan);
    const { restartTutorial, tutorialEnabled } = useContext(TutorialContext);

    // View state
    const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
    const [currentFolder, setCurrentFolder] = useState<MyModelsFolder | null>(null);
    const [filter, setFilter] = useState<'all' | 'mymodels' | 'shared'>('all');

    // Creation modal state
    const [createModelModalOpen, setCreateModelModalOpen] = useState(false);
    const [createFolderModalOpen, setCreateFolderModalOpen] = useState(false);
    const [newItemName, setNewItemName] = useState('');

    // Data fetching via TanStack Query
    const { data: folderTree, isLoading } = useModels();
    const state: MyModelsPageState = useMemo(
        () => (folderTree ? mapApiFolderTree(folderTree) : DEFAULT_STATE),
        [folderTree],
    );

    // Mutations
    const createModelMutation = useCreateModel();
    const createFolderMutation = useCreateFolder();
    const updateModelMutation = useUpdateModel();
    const updateFolderMutation = useUpdateFolder();
    const moveSharedModelMutation = useMoveSharedModel();

    // Cache invalidation callback for child components still using apiService
    const queryClient = useQueryClient();
    const reloadState = useCallback(
        () => queryClient.invalidateQueries({ queryKey: MODELS_QUERY_KEY }),
        [queryClient],
    );

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////
    //                                              API                                                      //
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////

    const moveFolder = useCallback((folder: MyModelsFolder, newFolder: MyModelsFolder) => {
        updateFolderMutation.mutate({
            id: folder.id,
            name: folder.name,
            parentId: newFolder.id,
        });
    }, [updateFolderMutation]);

    const moveModel = useCallback((item: MyModelMetadata & { isSharedContext?: boolean }, newFolder: MyModelsFolder) => {
        if (item.isSharedContext) {
            moveSharedModelMutation.mutate({
                id: item.id,
                folderId: newFolder.id,
            });
        } else {
            updateModelMutation.mutate({
                id: item.id,
                name: item.name,
                folderId: newFolder.id,
            });
        }
    }, [updateModelMutation, moveSharedModelMutation]);

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////
    //                                       Navigation Helpers                                              //
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////

    const handleNavigateToRoot = useCallback(() => {
        setCurrentFolder(null);
    }, []);

    const handleNavigateBack = useCallback(() => {
        if (!currentFolder) return;

        if (currentFolder.folderId === null || currentFolder.folderId.toString() === state.rootFolderId) {
            handleNavigateToRoot();
        } else {
            const parentFolder = state.folders[currentFolder.folderId.toString()];
            setCurrentFolder(parentFolder);
        }
    }, [currentFolder, state, handleNavigateToRoot]);

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////
    //                                       Visible Items Helper                                            //
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////

    const getVisibleItems = () => {
        const folderId = currentFolder ? currentFolder.id.toString() : state.rootFolderId;
        const folder = state.folders[folderId];
        if (!folder) return { folders: [], models: [] };

        const folders = (folder.folderChildrensIds || []).map(id => ({
            data: state.folders[id.toString()],
            key: `folder-${id}`,
        }));

        const models: Array<{ data: MyModelMetadata; isShared: boolean; key: string }> = [];

        if (filter !== 'shared') {
            models.push(...(folder.modelChildrensIds || []).map(id => ({
                data: state.models[id.toString()],
                isShared: false,
                key: `model-${id}`,
            })));
        }

        if (filter !== 'mymodels') {
            models.push(...(folder.sharedModelChildrensIds || []).map(id => ({
                data: state.sharedModels[id.toString()],
                isShared: true,
                key: `shared-${id}`,
            })));
        }

        return { folders, models };
    };

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////
    //                                       Creation Handlers                                               //
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////

    const canCreateModel = commercialPlan && commercialPlan.is_currently_active && (commercialPlan.models_remaining_count === -1 || commercialPlan.models_remaining_count > 0);

    const handleCreateModel = () => {
        if (!newItemName.trim().length) return;

        const parentFolderId = currentFolder
            ? currentFolder.id
            : parseInt(state.rootFolderId);

        createModelMutation.mutate(
            { name: newItemName.trim(), folderId: parentFolderId },
            {
                onSuccess: () => {
                    useAppStore.getState().decrementModelsRemaining();
                    setCreateModelModalOpen(false);
                    setNewItemName('');
                },
            },
        );
    };

    const handleCreateFolder = () => {
        if (!newItemName.trim().length) return;

        const parentFolderId = currentFolder
            ? currentFolder.id
            : parseInt(state.rootFolderId);

        createFolderMutation.mutate(
            { name: newItemName.trim(), parentId: parentFolderId },
            {
                onSuccess: () => {
                    setCreateFolderModalOpen(false);
                    setNewItemName('');
                },
            },
        );
    };

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////
    //                                       Render Helpers                                                  //
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////

    const renderBreadcrumb = () => {
        if (!currentFolder) {
            return (
                <div className="flex items-center gap-1">
                    <span className="text-sm font-semibold text-slate-700">Models</span>
                </div>
            );
        }

        const pathList: MyModelsFolder[] = [];
        let cFolder: MyModelsFolder | undefined = currentFolder;
        while (cFolder && cFolder.folderId !== null) {
            pathList.unshift(cFolder);
            if (cFolder.folderId.toString() === state.rootFolderId) break;
            cFolder = state.folders[cFolder.folderId.toString()];
        }

        const rootFolder = state.folders[state.rootFolderId];

        return (
            <div className="flex items-center gap-1">
                <FolderPathButton
                    folder={rootFolder}
                    name="Models"
                    setCurrentFolder={() => handleNavigateToRoot()}
                    moveFolder={moveFolder}
                    moveModel={moveModel}
                />
                {pathList.map((folder) => (
                    <React.Fragment key={folder.id}>
                        <span className="text-slate-300 text-sm">/</span>
                        <FolderPathButton
                            folder={folder}
                            name={folder.name}
                            setCurrentFolder={setCurrentFolder}
                            moveFolder={moveFolder}
                            moveModel={moveModel}
                        />
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const renderFilterChips = () => (
        <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden">
            {([
                { value: 'all', label: 'All' },
                { value: 'mymodels', label: 'My Models' },
                { value: 'shared', label: 'Shared with Me' },
            ] as const).map(({ value, label }) => (
                <button
                    key={value}
                    className={cn(
                        "px-3 py-1.5 text-xs font-medium transition-colors",
                        filter === value
                            ? "bg-slate-900 text-white"
                            : "bg-white text-slate-500 hover:bg-slate-50"
                    )}
                    onClick={() => setFilter(value)}
                >
                    {label}
                </button>
            ))}
        </div>
    );

    const renderViewToggle = () => (
        <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden">
            <button
                className={cn(
                    "px-3 py-1.5 transition-colors",
                    viewMode === 'list'
                        ? "bg-slate-900 text-white"
                        : "bg-white text-slate-500 hover:bg-slate-50"
                )}
                onClick={() => setViewMode('list')}
            >
                <span className="material-symbols-outlined text-[18px]">format_list_bulleted</span>
            </button>
            <button
                className={cn(
                    "px-3 py-1.5 transition-colors",
                    viewMode === 'grid'
                        ? "bg-slate-900 text-white"
                        : "bg-white text-slate-500 hover:bg-slate-50"
                )}
                onClick={() => setViewMode('grid')}
            >
                <span className="material-symbols-outlined text-[18px]">grid_view</span>
            </button>
        </div>
    );

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center py-16">
                    <span className="text-sm text-slate-400">Loading models...</span>
                </div>
            );
        }

        const { folders, models } = getVisibleItems();
        const hasFolders = folders.length > 0;
        const hasModels = models.length > 0;

        if (!hasFolders && !hasModels) return <EmptyState filter={filter} />;

        if (viewMode === 'list') {
            return (
                <div className="min-h-[300px]">
                    {folders.map(({ data, key }) => (
                        <div key={key}>
                            <ModelListFolder
                                folder={data}
                                state={state}
                                reloadState={reloadState}
                                setCurrentFolder={setCurrentFolder}
                                moveFolder={moveFolder}
                                moveModel={moveModel}
                            />
                            <div className="border-b border-slate-100"/>
                        </div>
                    ))}
                    {models.map(({ data, isShared, key }) => (
                        <div key={key}>
                            <ModelListItem
                                model={data}
                                state={state}
                                reloadState={reloadState}
                                isSharedContext={isShared}
                                stateMyModels={isShared ? state : null}
                            />
                            <div className="border-b border-slate-100"/>
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="px-6 py-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {folders.map(({ data, key }) => (
                        <ModelGridFolder
                            key={key}
                            folder={data}
                            state={state}
                            reloadState={reloadState}
                            setCurrentFolder={setCurrentFolder}
                            moveFolder={moveFolder}
                            moveModel={moveModel}
                        />
                    ))}
                    {models.map(({ data, isShared, key }) => (
                        <ModelGridItem
                            key={key}
                            model={data}
                            state={state}
                            reloadState={reloadState}
                            isSharedContext={isShared}
                            stateMyModels={isShared ? state : null}
                        />
                    ))}
                </div>
            </div>
        );
    };

    const renderCreateModelModal = () => (
        <TailwindModal
            isOpen={createModelModalOpen}
            onClose={() => { setCreateModelModalOpen(false); setNewItemName(''); }}
            title="Create New Model"
            description="Enter a name for your new financial model."
            size="sm"
            footer={
                <>
                    <TailwindButton
                        variant="ghost"
                        onClick={() => { setCreateModelModalOpen(false); setNewItemName(''); }}
                    >
                        Cancel
                    </TailwindButton>
                    <TailwindButton
                        variant="primary"
                        icon="add"
                        disabled={!newItemName.trim().length || createModelMutation.isPending}
                        onClick={handleCreateModel}
                    >
                        Create Model
                    </TailwindButton>
                </>
            }
        >
            <TailwindInput
                label="Model Name"
                placeholder="e.g., Infrastructure 1"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateModel(); }}
                fullWidth
                autoFocus
            />
        </TailwindModal>
    );

    const renderCreateFolderModal = () => (
        <TailwindModal
            isOpen={createFolderModalOpen}
            onClose={() => { setCreateFolderModalOpen(false); setNewItemName(''); }}
            title="Create New Folder"
            description="Enter a name for your new folder."
            size="sm"
            footer={
                <>
                    <TailwindButton
                        variant="ghost"
                        onClick={() => { setCreateFolderModalOpen(false); setNewItemName(''); }}
                    >
                        Cancel
                    </TailwindButton>
                    <TailwindButton
                        variant="primary"
                        icon="create_new_folder"
                        disabled={!newItemName.trim().length || createFolderMutation.isPending}
                        onClick={handleCreateFolder}
                    >
                        Create Folder
                    </TailwindButton>
                </>
            }
        >
            <TailwindInput
                label="Folder Name"
                placeholder="e.g., Client Projects"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); }}
                fullWidth
                autoFocus
            />
        </TailwindModal>
    );

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////
    //                                              Render                                                   //
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////

    return (
        motionify(
            <div className="flex-grow bg-white min-h-full">
                {/* Page Header */}
                <div className="px-8 py-6 border-b border-slate-100 bg-white sticky top-0 z-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                        <div className="flex-1">
                            <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                                My Models
                            </h2>
                            <p className="text-sm text-slate-500 mt-2">
                                Manage your financial models and shared workspaces
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <TailwindButton
                                variant="dark"
                                icon="menu_book"
                                size="sm"
                                disabled={tutorialEnabled}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    apiService.enableMainTutorial().then(async(response) => {
                                        if (response) {
                                            restartTutorial();
                                        }
                                    })
                                }}
                            >
                                Tutorial
                            </TailwindButton>
                        </div>
                    </div>
                </div>

                {/* Page Content */}
                <div className="px-8 py-6 flex flex-col gap-6">
                    <DndProvider backend={HTML5Backend} options={{ enableMouseEvents: true }}>
                        <ModelDragLayer />
                        <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                            {/* Breadcrumb Bar */}
                            <div className="px-6 py-3 flex items-center gap-2 bg-white border-b border-slate-100">
                                {currentFolder && (
                                    <button
                                        className="size-8 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center text-slate-500 hover:text-slate-700"
                                        onClick={handleNavigateBack}
                                        aria-label="Go back"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                                    </button>
                                )}
                                {renderBreadcrumb()}
                            </div>

                            <TutorialStep tutorial='main' identifier='models_management' display='inline-block' position="center">
                                <div></div>
                            </TutorialStep>

                            {/* Toolbar */}
                            <div className="px-6 py-4 flex items-center gap-3 border-b border-slate-100 bg-slate-50/30">
                                    {renderFilterChips()}

                                <TutorialStep tutorial='main' identifier='models_creation' display='inline-block' position="none" marginLeft={20}>
                                    <TailwindButton
                                        variant="primary"
                                        size="sm"
                                        icon={canCreateModel ? "add" : "lock"}
                                        disabled={!canCreateModel}
                                        onClick={() => {
                                            if (canCreateModel) {
                                                setNewItemName('');
                                                setCreateModelModalOpen(true);
                                            }
                                        }}
                                    >
                                        New Model
                                    </TailwindButton>
                                </TutorialStep>

                                <TailwindButton
                                    variant="secondary"
                                    size="sm"
                                    icon="create_new_folder"
                                    onClick={() => {
                                        setNewItemName('');
                                        setCreateFolderModalOpen(true);
                                    }}
                                >
                                    New Folder
                                </TailwindButton>

                                <div className="flex-grow"/>
                                {renderViewToggle()}
                            </div>

                            {/* Content */}
                            <div className="bg-white">
                                {renderContent()}
                            </div>
                        </div>
                    </DndProvider>
                </div>

                {/* Creation Modals */}
                {renderCreateModelModal()}
                {renderCreateFolderModal()}
            </div>
        )
    )
}
